<template>
  <div class="modal invite-other-modal" ref="modalRef" data-bs-backdrop="static" tabindex="-1" v-on:[showEvent]="reset" v-on:[shownEvent]="onShown">
    <div class="modal-dialog modal-dialog-centered">
      <form ref="formRef" class="modal-content" novalidate>
        <div class="modal-header">
          <h3 class="modal-title">
            Add someone
          </h3>
          <button type="button" class="btn-close" v-bind="{ disabled: busyRef }" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <label>
            Add an existing profile or user:
          </label>
          <ProfileSelector ref="profileSelectorRef"
            v-model="selectedPersonIdRef" 
            :disabled="busyRef"
            :post-filter="filterToProfilePersonsAsync"/>
          <div class="option-area">
            <div v-if="profileGroupRef?.isUserProfile && enableAddRef" class="form-check profile-only">
              <input type="checkbox" id="profileOnlyChk" class="form-check-input" v-model="profileOnlyRef"/>
              <label class="form-check-label" for="profileOnlyChk">
                  Share profile only
              </label>
            </div>
            <div class="invalid-feedback">{{ selectedPersonErrorRef }}</div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-primary add-new" @click="addNew">
            New person
          </button>
          <button type="submit" class="btn btn-primary add" :disabled="!enableAddRef" @click.prevent.stop="addAction">
            <img v-if="isInviteActionRef" src="@/assets/icons/mail_gray_60.png"/>
            {{ actionTextRef }}
          </button>
          <button type="button" class="btn btn-outline-primary" :disabled="busyRef" data-bs-dismiss="modal">
            Cancel
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-title img {
  margin-right: 0.25rem;
  width: 2.5rem;
  filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
}

.modal-body {
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.option-area {
  min-height: 2.5rem;
}

.profile-only {
  margin-top: 0.25rem;
  margin-left: 0.5rem;
}

.invalid-feedback {
  margin: 0;
  display: block;
}

.modal-footer {
  button {
    min-width: 90px;
  }

  button.add-new {
    margin-left: 0.5rem;
    margin-right: auto;
  }

  button.add img {
    margin-right: 0.25rem;
    width: 24px;
    filter: invert(1) brightness(2);
  }
}
</style>

<script lang="ts" setup>
import { computed, inject, nextTick, ref } from 'vue'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { PotentialMember } from './FamilyGroupMemberManager'
import { hideModalAsync } from '@/util/AppUtil'
import { ViewPerson } from '@/rd/ResearchDataModel'
import { useTokenManager } from '@/auth/TokenManager'
import { Modal as BSModal } from 'bootstrap'
import { usePersonStore } from '@/rd/PersonStore'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import { FamilyGroupMember, GroupMemberRole, ItemPermissions } from '@/gp/GroupAdminModel'
import { ProfileSharingState } from './ProfileSharingState'
import { useFamilyGroupMemberStore } from '@/gp/FamilyGroupMemberStore'
import ProfileSelector from '@/manage/profiles/ProfileSelector.vue'

const props = defineProps<{
  familyGroupId: string
  potentialMembers: PotentialMember[]
}>()

const emit = defineEmits<{
  (e: 'invite', profilePersonId: string, profileOnly: boolean): void
  (e: 'addNew'): void
}>()

defineExpose({
  open: () => BSModal.getOrCreateInstance(modalRef.value!).show()
})

const showEvent = 'show.bs.modal'
const shownEvent = 'shown.bs.modal'

enum AddAction {
  AddProfile,
  AddUser,
  InviteProfile,
  InviteUser,
}

const actionText = {
  [AddAction.AddProfile]: "Add profile",
  [AddAction.AddUser]: "Add user",
  [AddAction.InviteProfile]: "Request share",
  [AddAction.InviteUser]: "Invite",
}

const busyRef = ref(false)
const selectedPersonIdRef = ref<string>()
const profileOnlyRef = ref(false)

const modalRef = ref<InstanceType<typeof HTMLElement>>()
const profileSelectorRef = ref<InstanceType<typeof ProfileSelector>>()

const personStore = usePersonStore()
const dataGroupStore = useDataGroupStore()
const profileSharingState = inject<ProfileSharingState>('profileSharingState')!
const memberStore = useFamilyGroupMemberStore()

const selectedPersonRef = computed(() => personStore.getLoadedPersonList([selectedPersonIdRef.value]).at(0))
const profileGroupIdRef = computed(() => CompositeId.getGroupId(selectedPersonIdRef.value))
const profileGroupRef = computed(() => dataGroupStore.getLoadedGroup(profileGroupIdRef.value))
const profilePermissions = computed(() => dataGroupStore.getAsyncPermissions(profileGroupIdRef.value, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const canModifyProfileRef = computed(() => (profilePermissions.value & ItemPermissions.Modify) > 0)
const shouldAddUserRef = computed(() => profileGroupRef.value?.isUserProfile && !profileOnlyRef.value)
const addActionRef = computed(() => canModifyProfileRef.value ? (shouldAddUserRef.value ? AddAction.AddUser : AddAction.AddProfile)
  : shouldAddUserRef.value ? AddAction.InviteUser : AddAction.InviteProfile)
const canAddUserRef = computed(() => profileGroupRef.value?.isUserProfile && canModifyProfileRef.value)
const potentialMemberRef = computed(() => 
  props.potentialMembers.find(pm => pm.profilePerson && selectedPersonIdRef.value == pm.profilePerson.id!))
const enableAddRef = computed(() => selectedPersonIdRef.value && !selectedPersonErrorRef.value)
const isInviteActionRef = computed(() => selectedPersonIdRef.value && !canModifyProfileRef.value)
const actionTextRef = computed(() => selectedPersonIdRef.value ? actionText[addActionRef.value] : "Add")

const selectedPersonErrorRef = computed(() => {
  if (!selectedPersonRef.value)
    return undefined

  if (potentialMemberRef.value?.member)
    return 'This person is already a member of the group.'

  if (potentialMemberRef.value?.hasPendingGroupInvite)
    return 'This person has already been invited.'

  if (potentialMemberRef.value?.isProfileShared)
    return 'This profile is already visible to the group.'

  return undefined
})

async function filterToProfilePersonsAsync(vps: ViewPerson[]) {
  const groupIds = new Set<string>()
  vps.forEach(vp => vp.groupIds.forEach(gid => groupIds.add(gid)))

  const personGroups = await dataGroupStore.getGroupListAsync([...groupIds])
  const profilePersonIds = new Set<string>(personGroups.filter(dg => dg && dg.isProfile).map(g => g.startItemId!))

  return vps.filter(vp => vp.matchIds.some(id => profilePersonIds.has(id)))
}

async function addAction() {
  await hideModalAsync(modalRef.value!)
  if (canModifyProfileRef.value) {
    // share the profile with the group
    profileSharingState.shareProfiles([profileGroupIdRef.value!], true)

    // if not profile-only, add the user to the group too
    if (profileGroupRef.value?.isUserProfile && !profileOnlyRef.value) {
      const fm = new FamilyGroupMember()
      fm.familyGroupId = props.familyGroupId
      fm.userId = profileGroupRef.value!.ownerId
      fm.role = GroupMemberRole.Member
      await memberStore.addAsync(fm)
    }
  }
  else {
    emit('invite', selectedPersonIdRef.value!, profileOnlyRef.value)
  }
}

function reset() {
  selectedPersonIdRef.value = undefined
  profileOnlyRef.value = false
  busyRef.value = false
}

async function onShown() {
  await nextTick()
  profileSelectorRef.value?.focus()
}

async function addNew() {
  await hideModalAsync(modalRef.value!)
  emit('addNew')
}
</script>
